import * as React from 'react';
import { memo, useMemo } from 'react';

import styled, { css } from 'styled-components';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Colors } from 'services/ColorService';
import { getFontFamily } from 'services/FontService';

import { ToastTypes } from 'types';

import { Icon, IconsNames } from 'atoms';

export interface IToastComponent {
  type: ToastTypes;
  title: string;
  text?: string;
  className?: string;
  /** Optional inline action (spec 0030 r2) — see IToastData. */
  actionLabel?: string;
  onAction?: () => void;
  /** Явный крестик: клик по самой плашке тоже закрывает, но это неочевидно. */
  onClose?: () => void;
}

export const ToastComponent = memo(
  ({
    type,
    title,
    text,
    className,
    actionLabel,
    onAction,
    onClose,
  }: IToastComponent) => {
    const { t } = useTranslation();
    const toastIconName = useMemo(() => {
      switch (type) {
        case ToastTypes.success:
          return text ? IconsNames.Success : IconsNames.CircleSuccess;
        case ToastTypes.info:
          return text ? IconsNames.Info : IconsNames.InfoIcon;
        case ToastTypes.warning:
          return text ? IconsNames.Warning : IconsNames.WarningIcon;
        case ToastTypes.error:
          return text ? IconsNames.Error : IconsNames.ErrorIcon;
        default:
          return IconsNames.Info;
      }
    }, [type, text]);

    return (
      <div className={className}>
        <InnerWrapper $type={type} $text={text}>
          {text ? (
            <>
              <ToastIcon name={toastIconName} />
              <Content>
                <Title>{title}</Title>
                <Text>{text}</Text>
              </Content>
            </>
          ) : (
            <>
              <ToastIconSmall name={toastIconName} />
              <ContentSmall>
                <TitleSmall>{title}</TitleSmall>
              </ContentSmall>
            </>
          )}
          {actionLabel && onAction ? (
            // stopPropagation: the whole toast is a dismiss target
            // (ToastContainer), so without it the click would close the toast
            // and never run the action.
            <ActionButton
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAction();
              }}
            >
              {actionLabel}
            </ActionButton>
          ) : null}
          {onClose ? (
            <CloseButton
              type="button"
              aria-label={`${t('button.close')}`}
              title={`${t('button.close')}`}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </CloseButton>
          ) : null}
        </InnerWrapper>
      </div>
    );
  },
);

const InnerWrapper = styled.div<{ $type: ToastTypes; $text?: string }>`
  display: flex;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  padding: ${({ $text }) => ($text ? `16px 24px` : `8px 16px`)};
  border-width: 1px;
  border-style: solid;

  ${({ $type }) => {
    switch ($type) {
      case ToastTypes.success:
        return css`
          border-color: ${Colors.SuccessDefault};
          background-color: #ebf9eb;
        `;
      case ToastTypes.info:
        return css`
          border-color: ${Colors.Blue};
          background-color: ${Colors.BasicBlueLight};
        `;
      case ToastTypes.warning:
        return css`
          border-color: ${Colors.WarningDefault};
          background-color: #fef8e8;
        `;
      case ToastTypes.error:
        return css`
          border-color: ${Colors.ErrorDefault};
          background-color: #fdebeb;
        `;
      default:
        return css``;
    }
  }};
`;

const ToastIcon = styled((props) => <Icon {...props} />)`
  width: 24px;
  height: 24px;
`;

const ToastIconSmall = styled((props) => <Icon {...props} />)`
  width: 16px;
  height: 16px;
  margin: 4px 0;
  flex-shrink: 0;
`;

const Content = styled.div`
  margin-left: 16px;
`;

const ContentSmall = styled.div`
  margin-left: 8px;
`;

const Title = styled.span`
  font-weight: 600;
  color: ${Colors.Black};
  font-family: ${getFontFamily('semibold')};
  font-size: 16px;
  line-height: 24px;
`;

const TitleSmall = styled(Title)`
  font-size: 14px;
  line-height: 24px;
`;

const Text = styled.p`
  margin-top: 8px;
  font-family: ${getFontFamily('regular')};
  font-size: 12px;
  line-height: 16px;
`;

const ActionButton = styled.button`
  flex-shrink: 0;
  align-self: center;
  margin-left: 12px;
  padding: 4px 10px;
  border: 1px solid currentColor;
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-family: ${getFontFamily('semibold')};
  font-size: 13px;
  line-height: 18px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  align-self: flex-start;
  width: 24px;
  height: 24px;
  margin-left: 8px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${Colors.Gray};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: rgba(0, 0, 0, 0.06);
    color: ${Colors.Black};
  }
`;
